<template lang="pug">
.draw-area-mobile(:class='{ expanded: expanded }')
  .draw-area-mobile__content
    button.btn-back(@click='close()')
      icon(name='chevron-left')
    button.btn-clear(@click='clearDraw()' v-if='draw')
      icon(name='trash-can')
    p.intro {{ $t('draw_with_fingers_or_pen', { label: label }) }}
    .line {{ $t('draw_your') }} <span class="text-lowercase"> {{ label }}</span>
    .canvas-block(ref='canvasBlock')
      canvas.canvas(
        :height='`${height}`'
        :width='`${width}`'
        ref='canvas'
        v-on:mousedown='clickToDraw($event.layerX, $event.layerY)'
        v-on:mousemove='drawing($event.layerX, $event.layerY)'
        v-on:mouseup='stopDrawing()'
        v-on:touchcancel='onTouchEnd'
        v-on:touchend='onTouchEnd'
        v-on:touchmove.prevent='onTouchMove'
        v-on:touchstart.prevent='onTouchStart'
      )
    button.btn-apply.btn.btn-primary(@click='apply') {{ $t('button.apply') }}
</template>
<script>
import Icon from '@/ui/atoms/Icon.vue';

export default {
  components: {
    Icon,
  },
  props: {
    label: {
      type: String,
      default: 'Assinatura',
    },
  },
  data() {
    return {
      ready: false,
      width: 200,
      height: 100,
      isDrawing: false,
      drawCordX: '',
      drawCordY: '',
      context: '',
      draw: '',
      blob: '',
      expanded: false,
    };
  },
  mounted() {
    this.context = this.$refs.canvas.getContext('2d');
    this.canvasResizeObserver = new ResizeObserver(() => {
      this.setCanvasSize();
    });
    this.canvasResizeObserver.observe(this.$refs.canvasBlock);
  },
  beforeDestroy() {
    this.canvasResizeObserver.disconnect();
  },
  methods: {
    getTouchPosition(e) {
      const canvas = this.$refs.canvas;
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches[0];

      return {
        x: (touch.clientX - rect.left) * (canvas.width / rect.width),
        y: (touch.clientY - rect.top) * (canvas.height / rect.height),
      };
    },
    onTouchMove(e) {
      const { x, y } = this.getTouchPosition(e);
      this.drawing(x, y);
    },
    onTouchStart(e) {
      const { x, y } = this.getTouchPosition(e);
      this.clickToDraw(x, y);
    },
    onTouchEnd() {
      this.drawing(this.drawCordX, this.drawCordY);
      this.stopDrawing();
    },
    clickToDraw(x, y) {
      this.drawCordX = x;
      this.drawCordY = y;
      this.isDrawing = true;
    },
    clearDraw() {
      this.context.clearRect(0, 0, this.width, this.height);
      this.draw = '';
    },
    drawing(x, y) {
      if (!this.isDrawing) {
        return true;
      }
      this.context.beginPath();
      this.context.lineWidth = Math.round(this.width * 0.008);
      this.context.lineCap = 'round';
      this.context.strokeStyle = 'black';
      this.context.moveTo(this.drawCordX, this.drawCordY);

      this.drawCordX = x;
      this.drawCordY = y;

      this.context.lineTo(x, y);
      this.context.stroke();
      return true;
    },
    stopDrawing() {
      this.isDrawing = false;
      this.context.canvas.toBlob((blob) => {
        this.blob = blob;
        this.blobToBase64(blob).then((draw) => {
          this.draw = draw;
        });
      });
    },
    async setCanvasSize() {
      if (!this.expanded || !this.context) {
        return;
      }

      const block = this.$refs.canvasBlock;
      const width = Math.floor(block.clientWidth);
      const height = Math.floor(block.clientHeight);

      if (
        !width ||
        !height ||
        (width === this.width && height === this.height)
      ) {
        return;
      }

      const previous = document.createElement('canvas');
      previous.width = this.$refs.canvas.width;
      previous.height = this.$refs.canvas.height;
      previous.getContext('2d').drawImage(this.$refs.canvas, 0, 0);

      const hadDrawing = Boolean(this.draw);
      this.isDrawing = false;
      this.width = width;
      this.height = height;

      await this.$nextTick();

      const scale = Math.min(width / previous.width, height / previous.height);
      const restoredWidth = previous.width * scale;
      const restoredHeight = previous.height * scale;

      this.context.drawImage(
        previous,
        (width - restoredWidth) / 2,
        (height - restoredHeight) / 2,
        restoredWidth,
        restoredHeight
      );

      if (hadDrawing) {
        this.stopDrawing();
      }
    },
    blobToBase64(blob) {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      return new Promise((resolve) => {
        reader.onloadend = () => {
          resolve(reader.result);
        };
      });
    },
    async expand() {
      this.expanded = !this.expanded;

      if (this.expanded) {
        await this.$nextTick();
        await this.setCanvasSize();
      }
    },
    apply() {
      if (!this.draw) {
        return;
      }
      this.$emit('change', {
        blob: this.blob,
        image: this.draw,
      });
      this.expand();
    },
    close() {
      this.expand();
    },
  },
};
</script>
<style lang="stylus">
.draw-area-mobile
  border: 1px solid #E5E5E5
  background-color: #fff
  border-radius: 5px
  min-width: 100%
  min-height: 100px
  position: relative
  .btn-clear
    top: 15%
    right: 9%
  .draw-area-mobile__content
    text-align: center
    display: flex
    flex-direction: column
    height: 100%
    box-sizing: border-box
    padding: 1rem 1rem 4.5rem
  .intro
    text-align: center
    position: static
    flex-shrink: 0
    min-height: 2rem
    width: auto
    margin: 0 2.5rem 1rem
    overflow-wrap: anywhere

  .canvas-block
    position: relative
    flex: 1
    min-height: 0
    width: 100%
  .canvas
    width: 100%
    height: 100%
    position: relative
    background-color: #fff
    margin: auto
  .line
    position: absolute
    border-top: 2px solid #aaaaaa
    top: auto
    bottom: 20%
    left: 0
    right: 0
    margin: auto 10%
    text-align: center
    padding-top: 1em
    z-index: 3
  .btn-apply
    width: 40%
    display: block
    margin: auto
    position: absolute
    left: 0
    right: 0
    z-index: 3
    bottom: 1em
  .btn-back
    position: absolute
    left: 1em
    top: 1em
    color: #013D52
    z-index: 3
    width: 2em
    height: 2em
  .btn-clear
    position: absolute
    right: 1rem
    top: 1rem
    color: #949494
    z-index: 3
    font-size: 1.3rem
    background-color: #fff

.signature-creator-modal .draw-area-mobile.expanded
  position: fixed
  top: 0
  right: 0
  bottom: 0
  left: 0
  width: auto
  min-width: 0
  min-height: 0
  margin: 0
  z-index: 1060
  background-color: #E5E5E5

  .draw-area-mobile__content
    height: 100%
    box-sizing: border-box
    padding: 3.5rem 1rem 4.5rem

  .intro
    top: .5rem
    left: 3.5rem
    right: 3.5rem
    width: auto
    margin: 0

  .canvas-block
    position: relative
    width: 100%
    height: 100%

  .canvas
    position: absolute
    top: 0
    left: 0
    display: block
    width: 100%
    height: 100%
    touch-action: none

  .line
    pointer-events: none
</style>
